import { infiniteQueryFetchNextPage } from '@repo/sdk'
import type { AuditTimelineEntry, V1AuditListEventsData } from '@repo/sdk/gen'
import { zV1AuditListEventsData } from '@repo/sdk/zod'
import { DataTableColumnSettings } from '@repo/ui/components/data-table/column-settings'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/components/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/components/data-table/types'
import { PageLayout } from '@repo/ui/components/page-layout'
import { QueryBreadcrumbs } from '@repo/ui/components/query-breadcrumbs'
import { RefreshButton } from '@repo/ui/components/refresh-button'
import { dateToEndOfDayIso, dateToStartOfDayIso } from '@repo/ui/dates'
import { Separator } from '@repo/ui/separator'
import { getErrorCode, raise, zodNumberMaxValue } from '@repo/ui/utils'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import {
	AuditActorCell,
	AuditChevronCell,
	AuditTimestampCell,
	AuditWhatCell
} from '@/routes/(private)/(shell)/admin/audit/-components/audit-cells'
import { AuditDetailPanel } from '@/routes/(private)/(shell)/admin/audit/-components/audit-detail-panel'
import { AuditEmpty } from '@/routes/(private)/(shell)/admin/audit/-components/audit-empty'
import { AuditFilters } from '@/routes/(private)/(shell)/admin/audit/-components/audit-filters'
import {
	type AuditCategory,
	auditActionsForCategories,
	auditBatchKindsForCategories
} from '@/routes/(private)/(shell)/admin/audit/-lib/audit-catalogue'
import {
	auditSearchSchema,
	isAuditFiltered
} from '@/routes/(private)/(shell)/admin/audit/-lib/audit-search'
import { auditEventsQueryOptions } from '@/routes/(private)/(shell)/admin/audit/-query/query-options'
import { usePageSize } from '@/stores/use-page-size'

export const Route = createFileRoute('/(private)/(shell)/admin/audit/')({
	component: AdminAudit,
	validateSearch: auditSearchSchema,
	staticData: {
		breadcrumb: 'admin-audit:page_title'
	}
})

/**
 * What has happened inside the administrator's own organization, newest first.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THIS PAGE OFFERS NO ACTIONS, AND THE ABSENCE IS THE MESSAGE.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * No edit, no delete, no export — and, more particularly, no menu of those with
 * the items greyed out. A disabled item says "this action exists and you lack
 * the permission", which is false twice over: nothing anywhere can rewrite an
 * audit row, because the application's database role is granted read and
 * nothing else, and no export exists to be permitted. The immutability is made
 * visible by there being nothing to click, so the row's own affordance is a
 * chevron into a read-only panel rather than a trailing menu.
 * Do not add one.
 *
 * **THE PERIMETER IS THE SESSION'S.** `list-events` takes no organization and
 * offers no way to name another, so this screen neither filters by one nor
 * shows a control that looks as though it could.
 *
 * **THE DEFAULT WINDOW IS THE LAST 30 DAYS, NOT THE WHOLE HISTORY.** Nothing in
 * the URL says so — `from`/`to`/`period` are all absent on a fresh visit — and
 * this component is what fills the gap: `effectiveFrom`/`effectiveTo` compute
 * the window from the clock when neither an explicit range nor `period: 'all'`
 * is present. `dateToStartOfDayIso`/`dateToEndOfDayIso` normalise the result to
 * day boundaries, which is what keeps the query key stable across renders on
 * the same calendar day despite `new Date()` differing by milliseconds each
 * time — see the two helpers.
 *
 * **THE TWO EMPTY STATES ARE DIFFERENT AND THE SERVER TELLS THEM APART.**
 * `meta.retention.requestedRange` says whether the period asked for sits inside
 * the window, straddles its edge, or is wholly older than it. This component
 * passes that word along to `AuditEmpty` and never recomputes it.
 *
 * Paging is the endpoint's cursor, consumed as an infinite query. A cursor the
 * server rejects is answered by restarting from the first page in silence:
 * `cursor_invalid` on `expired` is routine, and an error toast would report it
 * as a fault.
 */
function AdminAudit() {
	const { t } = useTranslation()
	const params = Route.useSearch()
	const queryClient = useQueryClient()
	const pageSize = usePageSize((state) => state.pageSize)

	// The row a click opened, kept mounted through the panel's own closing
	// transition rather than cleared to `null` with it — see
	// `AuditDetailPanel`'s note on why. Local to the page: which row is open
	// is not a fact worth a link, unlike every filter beside it.
	const [selectedEntry, setSelectedEntry] =
		useState<AuditTimelineEntry | null>(null)
	const [detailOpen, setDetailOpen] = useState(false)

	const hasExplicitRange =
		params.from !== undefined || params.to !== undefined
	const now = new Date()
	// Calendar-day subtraction rather than a fixed millisecond span, so a
	// DST transition inside the window does not shift it by an hour.
	const defaultFrom = new Date(now)
	defaultFrom.setDate(defaultFrom.getDate() - 29)
	// `period: 'all'` is the only way to ask for the unbounded history; any
	// other absence of an explicit range means the default 30-day window —
	// see `audit-search.ts` on why the two are never both set.
	const unbounded = !hasExplicitRange && params.period === 'all'
	let effectiveFrom: Date | undefined
	let effectiveTo: Date | undefined
	if (hasExplicitRange) {
		effectiveFrom = params.from
		effectiveTo = params.to
	} else if (!unbounded) {
		effectiveFrom = defaultFrom
		effectiveTo = now
	}

	const listQuery = {
		limit: Math.min(pageSize, MAX_PAGE_SIZE),
		// The day a person picked, widened to the instants that bound it.
		// Sending the bare midnight of `to` would silently exclude that
		// whole day, which is the boundary bug every date filter has once.
		from: dateToStartOfDayIso(effectiveFrom),
		to: dateToEndOfDayIso(effectiveTo),
		personId: params.personId,
		// The six coarse categories expanded to the catalogue's own action
		// set, sorted — see `auditActionsForCategories` on why the sort is
		// load-bearing for the query key. `undefined` when nothing is
		// selected, which is "every kind" rather than "none".
		actions:
			params.types === undefined
				? undefined
				: auditActionsForCategories(params.types),
		objectType: params.objectType,
		objectId: params.objectId,
		batchKinds: batchKindsFor(params)
	} satisfies Omit<NonNullable<V1AuditListEventsData['query']>, 'cursor'>

	const events = useInfiniteQuery(auditEventsQueryOptions(listQuery))
	const rows = events.data?.events ?? EMPTY_EVENTS
	const retention = events.data?.retention

	// Not an error to show. The cursor expired or the listing moved under it,
	// and the answer is another first page — which is what the load-more
	// handler asks the cache for.
	const cursorExpired = getErrorCode(events.error) === 'cursor_invalid'

	const columns: DataTableColumn<AuditTimelineEntry>[] = [
		{
			// Pinned, because it is the column every other one is read
			// against: an audit is a sequence before it is a set of facts.
			id: 'createdAt',
			header: t('admin-audit:columns.timestamp'),
			pinned: 'left',
			cell: (entry) => <AuditTimestampCell entry={entry} />,
			width: 168
		},
		{
			id: 'actor',
			header: t('admin-audit:columns.actor'),
			cell: (entry) => <AuditActorCell event={entry} />,
			width: 210
		},
		{
			// The widest column and the one allowed to truncate: every other
			// column is a fixed fact, this one is a sentence, and the
			// sentence is what a reader is here for.
			id: 'what',
			header: t('admin-audit:columns.what'),
			cell: (entry) => <AuditWhatCell entry={entry} />,
			flex: 1,
			minWidth: 240
		},
		{
			id: 'chevron',
			header: null,
			cell: () => <AuditChevronCell />,
			width: 36,
			hideable: false,
			pinnable: false,
			enableColumnMenu: false
		}
	]

	const columnsState = useDataTableColumnState(columns)

	/**
	 * One more page, and the one refusal that is not a failure.
	 *
	 * `cursor_invalid` says the cursor no longer addresses anything — it is
	 * older than a day, or the filters it was minted under have moved. The
	 * answer is to start again from the first page, silently: resetting the
	 * query drops the pages held and refetches with no cursor at all.
	 */
	async function loadNextPage() {
		const result = await infiniteQueryFetchNextPage(events)
		if (getErrorCode(result?.error) === 'cursor_invalid') {
			await queryClient.resetQueries({
				queryKey: makeQueryKeys.audit.index(listQuery)
			})
		}
	}

	return (
		<PageLayout
			actions={<RefreshButton query={events} />}
			aside={
				<AuditDetailPanel
					entry={selectedEntry}
					onClose={() => setDetailOpen(false)}
					open={detailOpen}
				/>
			}
			bar={<AuditFilters person={events.data?.person} />}
			breadcrumb={<QueryBreadcrumbs />}
		>
			<div className='flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-6 md:px-6'>
				<DataTable
					columns={columns}
					columnsState={columnsState}
					data={rows}
					footer={
						<>
							<DataTableColumnSettings
								columns={columns}
								columnsState={columnsState}
							/>
							<Separator
								className='my-3'
								orientation='vertical'
							/>
							<span>
								{t('admin-audit:total', {
									count: events.data?.total ?? 0
								})}
							</span>
						</>
					}
					loadMore={{
						hasMore: events.hasNextPage,
						loading: events.isFetchingNextPage,
						onLoadMore: loadNextPage
					}}
					onRowClick={(entry) => {
						setSelectedEntry(entry)
						setDetailOpen(true)
					}}
					rowHeight={48}
					state={{
						empty: (
							<AuditEmpty
								filtered={isAuditFiltered(params)}
								retention={retention}
							/>
						),
						error: events.isError && !cursorExpired,
						loading: events.isPending || events.isPlaceholderData
					}}
				/>
			</div>
		</PageLayout>
	)
}

const EMPTY_EVENTS: AuditTimelineEntry[] = []

/**
 * The `batchKinds` half of the listing's filters.
 *
 * A batch carries no object of its own, so a page narrowed to one object must
 * never surface one just because its kind happens to sit under a selected
 * category — checked first and ahead of `types` rather than folded in beside
 * it. Outside that scope, `types` including "content" asks for the kinds of
 * transfer summary alongside the handful of content events that still get a
 * row of their own; every other category combination excludes batches the
 * same way it already excludes the actions no category names.
 */
function batchKindsFor(params: {
	objectId?: string
	objectType?: string
	types?: AuditCategory[]
}) {
	if (params.objectId !== undefined || params.objectType !== undefined) {
		return undefined
	}
	return params.types === undefined
		? undefined
		: auditBatchKindsForCategories(params.types)
}

/**
 * The endpoint's own ceiling on a page, off the contract rather than retyped.
 * The rows-per-page preference is app-wide and its options are not bound to
 * this route's endpoint, so the two are reconciled here instead of by hoping
 * they stay in step.
 *
 * `raise` rather than a fallback: a schema that has lost its ceiling means the
 * contract changed shape, and a quietly substituted number would let this list
 * ask for pages the server refuses with a `400`.
 */
const MAX_PAGE_SIZE =
	zodNumberMaxValue(
		zV1AuditListEventsData.shape.query.unwrap().shape.limit
	) ?? raise('GET /audit/events lost its page-size ceiling')
